import { useState } from "react";
import { Menu, Phone, X } from "lucide-react";
import { useLanguage } from "../i18n";
import { LogoFull } from "./Logo";
import { CONTACT } from "../data/cars";

export const LanguageToggle = ({ compact = false }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div
      data-testid="language-toggle"
      className="flex items-center rounded-full border border-white/15 bg-white/5 p-0.5"
    >
      {["en", "es"].map((l) => (
        <button
          key={l}
          data-testid={`language-toggle-${l}`}
          onClick={() => setLang(l)}
          className={`rounded-full px-3 py-1 font-mono-accent text-[11px] font-bold uppercase tracking-widest transition-colors duration-300 ${
            lang === l ? "bg-[#E63946] text-white" : "text-[#9E9E9E] hover:text-white"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const Header = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const links = [
    { href: "#services", label: t.nav.services, id: "nav-services" },
    { href: "#community", label: t.nav.community, id: "nav-community" },
    { href: "#gallery", label: t.nav.gallery, id: "nav-gallery" },
    { href: "#faq", label: "FAQ", id: "nav-faq" },
    { href: "#contact", label: t.nav.contact, id: "nav-contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/70 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <a href="#top" data-testid="logo-link" aria-label="Family Auto Sales home">
          <LogoFull compact />
        </a>

        <nav className="hidden items-center gap-7 lg:flex" data-testid="desktop-nav">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.id}
              className="text-sm font-medium text-[#9E9E9E] transition-colors duration-300 hover:text-white"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <LanguageToggle />
          <a
            href={CONTACT.phoneHref}
            data-testid="header-call-button"
            className="flex items-center gap-2 rounded-full bg-[#E63946] px-4 py-2 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#FF4D5A]"
          >
            <Phone className="h-3.5 w-3.5" />
            {t.nav.call}
          </a>
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <LanguageToggle compact />
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            className="rounded-full border border-white/15 p-2 text-white"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="border-t border-white/10 bg-[#0A0B0E]/95 px-5 py-4 backdrop-blur-xl lg:hidden"
        >
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`mobile-${l.id}`}
              onClick={() => setOpen(false)}
              className="block border-b border-white/5 py-3 text-base font-medium text-[#F8F9FA]"
            >
              {l.label}
            </a>
          ))}
          <a
            href={CONTACT.phoneHref}
            data-testid="mobile-call-button"
            className="mt-4 flex items-center justify-center gap-2 rounded-full bg-[#E63946] px-4 py-3 text-sm font-semibold text-white"
          >
            <Phone className="h-4 w-4" />
            {t.nav.call} · {CONTACT.phone}
          </a>
        </div>
      )}
    </header>
  );
};

export default Header;
